import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useSelector } from "react-redux";
import { getThoughtOfTheDay, getGreeting } from "../../constants/quotes";
import { useGetTodayThoughtQuery } from "../../service";
import xavaLogo from "../../assets/images/xava-logo.png";
import "./welcomeOverlay.scss";

const GREET_FLAG = "xava:greet";
const AUTO_DISMISS_MS = 6500;

export const requestWelcomeGreeting = () => {
  try {
    sessionStorage.setItem(GREET_FLAG, "1");
  } catch (e) {
    /* storage unavailable — skip the moment, never block login */
  }
};

function WelcomeOverlay() {
  const [visible, setVisible] = useState(false);

  // Consume the flag in an effect, not in the useState initializer: the
  // lazy-loaded page suspends the first render pass and React discards it,
  // which would eat the flag before a committed render ever sees it.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(GREET_FLAG) === "1") {
        sessionStorage.removeItem(GREET_FLAG);
        setVisible(true);
      }
    } catch (e) {
      /* storage unavailable — skip the moment */
    }
  }, []);
  const reduceMotion = useReducedMotion();
  const userInfo = useSelector((state) => state?.authState.userInfo);
  const { data: thoughtRes } = useGetTodayThoughtQuery();
  const quote = thoughtRes?.data || getThoughtOfTheDay();
  const greeting = getGreeting();

  const dismiss = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(dismiss, AUTO_DISMISS_MS);
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Enter") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, dismiss]);

  const seq = (delay) =>
    reduceMotion
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2, delay: delay / 2 } }
      : {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="xv-welcome"
          role="dialog"
          aria-label="Welcome greeting"
          initial={false} // ink panel is solid from the first frame; only content animates
          exit={{ opacity: 0, transition: { duration: 0.45 } }}
          onClick={dismiss}
        >
          <div className="xv-welcome-aurora" aria-hidden="true">
            <span className="blob blob-a" />
            <span className="blob blob-b" />
            <span className="blob blob-c" />
          </div>

          <div className="xv-welcome-inner">
            <motion.div className="xv-welcome-logo" {...seq(0.1)}>
              <img src={xavaLogo} alt="Xava Web Services" />
            </motion.div>

            <motion.h1 className="xv-welcome-greeting" {...seq(0.45)}>
              {greeting}
              {userInfo?.fname ? `, ${userInfo.fname}` : ""}.
            </motion.h1>

            <motion.div className="xv-welcome-thought" {...seq(0.95)}>
              <span className="xv-welcome-kicker">Thought of the day</span>
              <blockquote>“{quote.text}”</blockquote>
              <cite>— {quote.author}</cite>
            </motion.div>

            <motion.button
              type="button"
              className="xv-welcome-cta"
              onClick={dismiss}
              {...seq(1.5)}
            >
              Let&apos;s get to work →
            </motion.button>
          </div>

          {!reduceMotion && (
            <motion.span
              className="xv-welcome-progress"
              aria-hidden="true"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: AUTO_DISMISS_MS / 1000, ease: "linear" }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default WelcomeOverlay;
